import { dataSourceHandleActions, inboxActions } from "_actions";
import { inboxConstants } from "_constants";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useLocationQueryParams from "components/v2/hooks/useLocationQueryParams";
import {
  channelIdsForGroups,
  groupChannels,
  parseInboxStatus,
  parseSentiments,
  toInboxDateRange,
  toSentimentParam,
} from "../inboxUtils";

export default function () {
  const initialized = useRef(false);
  const dispatch = useDispatch();
  const { params, setParams } = useLocationQueryParams();
  const offsetBase = 10;
  const [page, setPage] = useState(() => {
    const offset = Number(params.offset);
    return offset ? offset / offsetBase : 0;
  });
  const [dateRange, setDateRange] = useState(() =>
    toInboxDateRange(params.startDate, params.endDate),
  );
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [inboxStatus, setInboxStatus] = useState(() =>
    parseInboxStatus(params.inboxStatus),
  );
  const [selectedSentiments, setSelectedSentiments] = useState(() =>
    parseSentiments(params.sentiment),
  );
  const [searchTerm, setSearchTerm] = useState(params.searchString || "");
  const [waitForHandles, setWaitForHandles] = useState(true);

  const handles = useSelector(
    (state) => state.dataSourceHandles.dataSourceHandles,
  );

  const handlesLoading = useSelector(
    (state) => state.dataSourceHandles.loading,
  );
  const channelGroups = useMemo(() => groupChannels(handles), [handles]);
  const loading = useSelector((state) => state.inbox.loading);
  const messages = useSelector((state) => state.inbox.messages);
  const totalCount = messages.totalCount || 0;
  const statusCounts = messages.statusCounts;

  const filterParams = (
    status = inboxStatus,
    sentiments = selectedSentiments,
  ) => {
    const sentiment = toSentimentParam(sentiments);
    return {
      ...(status ? { inboxStatus: status } : { inboxStatus: null }),
      sentiment,
    };
  };

  const handleFetchMessages = (
    offset,
    range,
    labels = selectedLabels,
    search = searchTerm,
    status = inboxStatus,
    sentiments = selectedSentiments,
  ) => {
    if (!sentiments.length) {
      dispatch({
        type: inboxConstants.GET_ORG_INBOX_MESSAGES_SUCCESS,
        payload: { items: [], totalCount: 0 },
      });
      return;
    }

    const channelIds = channelIdsForGroups(channelGroups, labels);
    const channels = channelIds.length
      ? channelIds.join(",")
      : channelGroups.length
        ? "0"
        : null;
    const searchString = search?.trim();
    const sentiment = toSentimentParam(sentiments);

    dispatch(
      inboxActions.getOrgInboxMessages({
        offset,
        startDate: range.startDate,
        endDate: range.endDate,
        ...(channels ? { channels } : {}),
        ...(searchString ? { searchString } : {}),
        ...(status ? { inboxStatus: status } : {}),
        ...(sentiment ? { sentiment } : {}),
      }),
    );
  };

  const handlePageChange = (e, nextPage) => {
    const offset = nextPage * offsetBase;
    setPage(nextPage);
    setParams({ ...params, offset, ...dateRange, ...filterParams() });
    handleFetchMessages(offset, dateRange);
  };

  const handleDateChange = (startDate, endDate) => {
    const nextRange = toInboxDateRange(startDate, endDate);
    setDateRange(nextRange);
    setPage(0);
    setParams({ ...params, offset: 0, ...nextRange, ...filterParams() });
    handleFetchMessages(0, nextRange);
  };

  const handleChannelGroupsChange = (labels) => {
    setSelectedLabels(labels);
    setPage(0);
    setParams({ ...params, offset: 0, ...dateRange, ...filterParams() });
    handleFetchMessages(0, dateRange, labels);
  };

  const handleSearch = (value) => {
    const nextSearch = value?.trim() || "";
    setSearchTerm(nextSearch);
    setPage(0);
    setParams({
      ...params,
      ...dateRange,
      offset: 0,
      searchString: nextSearch || null,
      ...filterParams(),
    });
    handleFetchMessages(0, dateRange, selectedLabels, nextSearch);
  };

  const handleStatusChange = (status) => {
    setInboxStatus(status);
    setPage(0);
    setParams({
      ...params,
      ...dateRange,
      offset: 0,
      ...filterParams(status, selectedSentiments),
    });
    handleFetchMessages(0, dateRange, selectedLabels, searchTerm, status);
  };

  const handleSentimentsChange = (sentiments) => {
    setSelectedSentiments(sentiments);
    setPage(0);
    setParams({
      ...params,
      ...dateRange,
      offset: 0,
      ...filterParams(inboxStatus, sentiments),
    });
    handleFetchMessages(
      0,
      dateRange,
      selectedLabels,
      searchTerm,
      inboxStatus,
      sentiments,
    );
  };

  useEffect(() => {
    let organizationId;
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      organizationId = user?._embedded?.organization?.id;
    } catch (error) {
      organizationId = null;
    }

    if (!organizationId) {
      setWaitForHandles(false);
      return;
    }

    dispatch(dataSourceHandleActions.getAll({ organization: organizationId }));
  }, [dispatch]);

  useEffect(() => {
    if (initialized.current) return;
    if (waitForHandles && handlesLoading) return;

    initialized.current = true;
    const labels = channelGroups.map((group) => group.label);
    setSelectedLabels(labels);
    handleFetchMessages(page * offsetBase, dateRange, labels);
  }, [waitForHandles, handlesLoading, channelGroups]);

  return {
    loading,
    messages,
    totalCount,
    handlePageChange,
    handleDateChange,
    handleChannelGroupsChange,
    handleStatusChange,
    handleSentimentsChange,
    handleSearch,
    searchTerm,
    channelGroups,
    selectedChannelGroups: selectedLabels,
    inboxStatus,
    selectedSentiments,
    page,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    statusCounts,
  };
}
